import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormat } from "hooks/useFormat";
import { useTheme } from "@mui/material";
import Typography from "components/basic/typography";
import { useNavigate, useLocation } from "react-router-dom";
import { EvServiceInstallationData } from "../..";
import {
  useUpdateEvChargerServiceMutation,
  useDeleteEvChargerServiceMutation,
  useGetEvChargerServiceListQuery,
} from "generated/graphql";
import { useSnack } from "hooks/useSnack";
import useFilteredInstallationData from "../../utility/useFilterUncheckedFields";
import WizardPageLayout from "../../components/WizardPageLayout";
import WizardButton from "../../components/WizardButton";
import ReviewSummary from "../../components/ReviewSummary";

const SUMMARY_WIDTH = 640;

interface ReviewAndUpdateProps {
  data: EvServiceInstallationData;
}

const ReviewAndUpdate: React.FC<ReviewAndUpdateProps> = ({ data }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const showSnack = useSnack();
  const { t } = useTranslation("services");
  const format = useFormat();
  const { filterInstallationData } = useFilteredInstallationData();

  // Get serviceId from URL params
  const searchParams = new URLSearchParams(location.search);
  const serviceId = searchParams.get("serviceId");

  // Fetch service data if we have a serviceId
  const [{ data: serviceData }] = useGetEvChargerServiceListQuery({
    pause: !serviceId,
  });

  const transformServiceData = useCallback((service: any) => {
    const groupedFields = service.evChargerServiceFields.reduce(
      (acc: any, field: any) => {
        if (!acc[field.fieldName]) {
          acc[field.fieldName] = [];
        }
        acc[field.fieldName].push({
          id: field.id,
          optionName: field.fieldLabel,
          isChecked: field.isChecked,
          type: field.fieldType,
          price: field.price,
          enteredName: field.customLabel || "",
          duplicates: field.duplicates,
          placeholder: field.placeholder,
        });
        return acc;
      },
      {},
    );

    return {
      serviceOfferingSetup: {
        supportedEVSETypes: groupedFields.supportedEVSETypes || [],
        buildingServiced: groupedFields.buildingServiced || [],
        areaServiced: service.areaServiced,
      },
      chargerConfig: {
        evseType: groupedFields.evseType || [],
      },
      installationConfig: {
        cableLength: groupedFields.cableLength || [],
        inclusions: groupedFields.inclusions || [],
        addOnServices: groupedFields.addOnServices || [],
      },
      paymentAndAssumptionsConfigData: {
        paymentTypes: groupedFields.paymentTypes || [],
        quoteValidFor: service.quoteValidFor,
        paymentTerms: service.paymentTerms,
        assumptions: groupedFields.assumptions || [],
      },
    };
  }, []);

  // Find the specific service and transform data
  const foundService =
    serviceId &&
    serviceData?.getEVChargerServiceList?.find(
      (service: any) => service.id.toString() === serviceId,
    );

  const transformedServiceData = foundService
    ? transformServiceData(foundService)
    : null;

  // Check if form data has user selections (contains checked items)
  const hasUserEdits = useMemo(() => {
    const hasCheckedItems = (items: any[]) =>
      items?.some((item: any) => item.isChecked);

    return (
      hasCheckedItems(data.serviceOfferingSetup.supportedEVSETypes) ||
      hasCheckedItems(data.serviceOfferingSetup.buildingServiced) ||
      data.serviceOfferingSetup.areaServiced != null ||
      hasCheckedItems(data.chargerConfig.evseType) ||
      hasCheckedItems(data.installationConfig.cableLength) ||
      hasCheckedItems(data.installationConfig.inclusions) ||
      hasCheckedItems(data.installationConfig.addOnServices) ||
      hasCheckedItems(data.paymentAndAssumptionsConfigData.paymentTypes) ||
      data.paymentAndAssumptionsConfigData.quoteValidFor?.trim() ||
      data.paymentAndAssumptionsConfigData.paymentTerms?.trim() ||
      hasCheckedItems(data.paymentAndAssumptionsConfigData.assumptions)
    );
  }, [data]);

  // Use current form data if it has user edits, otherwise use fetched service data
  const displayData = hasUserEdits ? data : transformedServiceData || data;
  const originalService = foundService || null;

  const [{ fetching: updateFetching }, updateEVChargerService] =
    useUpdateEvChargerServiceMutation();

  const [{ fetching: deleteFetching }, deleteEVChargerService] =
    useDeleteEvChargerServiceMutation();

  const handleUpdate = useCallback(async () => {
    if (
      !originalService ||
      typeof originalService === "string" ||
      !originalService.id
    ) {
      console.error("No service ID available for update");
      return;
    }

    try {
      const result = await updateEVChargerService({
        id: originalService.id,
        options: { data: filterInstallationData(data) },
      });

      if (result.error) {
        console.error("Error updating EV charger service:", result.error);
        alert(t("wizard.review.update_failed"));
        return;
      }

      if (result.data?.updateEVChargerService.evChargerService) {
        showSnack(t("wizard.review.updated"), "success");
        navigate("/dashboard/serviceOfferings");
      } else if (result.data?.updateEVChargerService.errors) {
        console.error(
          "Update failed:",
          result.data.updateEVChargerService.errors,
        );
        alert(
          result.data.updateEVChargerService.errors[0]?.message ||
            t("wizard.review.update_failed_short"),
        );
      }
    } catch (error) {
      console.error("Unexpected error during update:", error);
      alert(t("wizard.review.unexpected"));
    }
  }, [
    t,
    originalService,
    updateEVChargerService,
    data,
    navigate,
    filterInstallationData,
    showSnack,
  ]);

  const handleDelete = useCallback(async () => {
    if (
      !originalService ||
      typeof originalService === "string" ||
      !originalService.id
    ) {
      console.error("No service ID available for deletion");
      return;
    }

    const confirmDelete = window.confirm(
      t("wizard.review.delete_confirm", { id: originalService.id }),
    );

    if (!confirmDelete) {
      return;
    }

    try {
      const result = await deleteEVChargerService({
        id: originalService.id,
      });

      if (result.error) {
        console.error("Error deleting EV charger service:", result.error);
        alert(t("wizard.review.delete_failed"));
        return;
      }

      if (result.data?.deleteEVChargerService.success) {
        // The server archives instead of deleting when quotes reference the
        // service, so surface its message rather than assuming "deleted".
        showSnack(
          result.data.deleteEVChargerService.message ||
            t("wizard.review.deleted"),
          "success",
        );
        navigate("/dashboard/serviceOfferings");
      } else {
        console.error(
          "Delete failed:",
          result.data?.deleteEVChargerService.message,
        );
        alert(
          result.data?.deleteEVChargerService.message ||
            t("wizard.review.delete_failed_short"),
        );
      }
    } catch (error) {
      console.error("Unexpected error during deletion:", error);
      alert(t("wizard.review.unexpected"));
    }
  }, [originalService, deleteEVChargerService, navigate, showSnack, t]);

  return (
    <WizardPageLayout
      title={
        originalService
          ? t("wizard.review.details_title", { id: originalService.id })
          : t("wizard.review.update_title")
      }
      subtitle={
        originalService ? (
          <Typography
            variant="h3"
            align="inherit"
            style={{ color: theme.palette.text.secondary }}
            sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" } }}
          >
            {t("wizard.review.dates", {
              created: format.date(parseInt(originalService.createdAt)),
              updated: format.date(parseInt(originalService.updatedAt)),
            })}
          </Typography>
        ) : undefined
      }
      contentMaxWidth={SUMMARY_WIDTH}
      actions={
        <>
          <WizardButton
            color="primary"
            onClick={() => navigate("/dashboard/serviceOfferings")}
          >
            {t("wizard.review.back")}
          </WizardButton>

          <WizardButton
            color="success"
            onClick={handleUpdate}
            loading={updateFetching}
          >
            {t("wizard.review.update")}
          </WizardButton>

          <WizardButton
            color="error"
            onClick={handleDelete}
            loading={deleteFetching}
          >
            {t("wizard.review.delete")}
          </WizardButton>
        </>
      }
    >
      <ReviewSummary
        data={displayData}
        onEdit={(page) =>
          navigate(`?currentPage=${page}&editMode=true&serviceId=${serviceId}`)
        }
      />
    </WizardPageLayout>
  );
};

export default ReviewAndUpdate;
